import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Alert,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Add as AddIcon,
  Edit as EditIcon,
  ContentCopy as CloneIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  FileDownload as CsvIcon,
  ContentPaste as CopyIcon,
  Search as SearchIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  AccountTree as TreeIcon,
  ErrorOutline as ErrorIcon,
  RestartAlt as ResetIcon,
  CloudDone as CloudDoneIcon,
  InfoOutlined as InfoIcon,
  Close as CloseIcon,
  DragIndicator as DragHandleIcon,
  ArrowUpward as MoveUpIcon,
  ArrowDownward as MoveDownIcon,
  SortByAlpha as SortAlphaIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import DashboardLayout from '../components/DashboardLayout';
import { ProcessConfigModal } from '../components/report/ProcessConfigModal';
import { useAppDispatch } from '../store';
import { showNotification } from '../store/notificationSlice';
import { apiService } from '../services/api';
import {
  BaseDataRecord,
  AttendanceInfoRecord,
  ProcessNode,
} from '../types/report';
import {
  getAvailableCsvColumns,
  flattenReportDataToCsv,
  downloadCsvFile,
  copyTextToClipboard,
} from '../utils/csvExporter';

interface TreeNodeWithDepth {
  node: ProcessNode;
  depth: number;
}

interface SortableColumnItemProps {
  id: string;
  index: number;
  isDetail: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}

const SortableColumnItem: React.FC<SortableColumnItemProps> = ({
  id,
  index,
  isDetail,
  onMoveUp,
  onMoveDown,
  onRemove,
  canMoveUp,
  canMoveDown,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
    zIndex: isDragging ? 999 : 'auto',
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 1.25,
        py: 0.75,
        bgcolor: isDragging ? 'action.selected' : 'background.paper',
        border: '1px solid',
        borderColor: isDragging ? 'primary.main' : 'divider',
        borderRadius: 1,
        mb: 0.75,
        boxShadow: isDragging ? 3 : 'none',
        '&:hover': { bgcolor: 'grey.50' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'grab',
            color: 'text.secondary',
            '&:hover': { color: 'primary.main' },
            touchAction: 'none',
          }}
        >
          <DragHandleIcon fontSize="small" />
        </Box>
        <Chip
          label={`#${index + 1}`}
          size="small"
          sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, minWidth: 32 }}
        />
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            fontSize: '0.82rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {id}
        </Typography>
        <Chip
          label={isDetail ? 'Attendance' : 'Employee'}
          size="small"
          variant="outlined"
          color={isDetail ? 'secondary' : 'default'}
          sx={{ height: 18, fontSize: '0.65rem', flexShrink: 0 }}
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
        <Tooltip title="Move Up">
          <span>
            <IconButton size="small" onClick={onMoveUp} disabled={!canMoveUp} sx={{ p: 0.5 }}>
              <MoveUpIcon sx={{ fontSize: '0.95rem' }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Move Down">
          <span>
            <IconButton size="small" onClick={onMoveDown} disabled={!canMoveDown} sx={{ p: 0.5 }}>
              <MoveDownIcon sx={{ fontSize: '0.95rem' }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Remove from sequence">
          <IconButton
            size="small"
            onClick={onRemove}
            sx={{ p: 0.5, color: 'text.secondary', '&:hover': { color: 'error.main' } }}
          >
            <CloseIcon sx={{ fontSize: '0.95rem' }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export const ReportDataSource: React.FC = () => {
  const dispatch = useAppDispatch();

  // Date Range state
  const [fromDate, setFromDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [toDate, setToDate] = useState<Dayjs | null>(dayjs().endOf('month'));

  // Pipeline tree state loaded from backend DynamoDB
  const [pipelineTree, setPipelineTree] = useState<ProcessNode[]>([]);
  const [isTreeLoading, setIsTreeLoading] = useState<boolean>(true);
  const [isTreeSaving, setIsTreeSaving] = useState<boolean>(false);

  // Active / Selected Node
  const [selectedNodeId, setSelectedNodeId] = useState<string>('base-data');

  // Execution state & Results
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<BaseDataRecord[] | null>(null);
  const [lastExecutedNodeId, setLastExecutedNodeId] = useState<string | null>(null);
  const [executionTimeMs, setExecutionTimeMs] = useState<number | null>(null);
  const [executionError, setExecutionError] = useState<string | null>(null);

  // Search & Pagination in Autopsy table
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [expandedRowIds, setExpandedRowIds] = useState<Record<string, boolean>>({});

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalNode, setModalNode] = useState<ProcessNode | null>(null);
  const [modalParentId, setModalParentId] = useState<string>('base-data');
  const [modalParentName, setModalParentName] = useState<string>('Base Data');
  const [infoModalEmployee, setInfoModalEmployee] = useState<any | null>(null);

  // CSV Export Modal state
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [csvModalAction, setCsvModalAction] = useState<'download' | 'copy'>('download');
  const [selectedCsvCols, setSelectedCsvCols] = useState<string[]>([]);
  const [csvColSearch, setCsvColSearch] = useState('');
  const [avoidAbsent, setAvoidAbsent] = useState<boolean>(false);

  // Load pipeline hierarchy from backend DynamoDB on mount
  useEffect(() => {
    let isMounted = true;
    const loadPipelineTree = async () => {
      try {
        setIsTreeLoading(true);
        const res = await apiService.getReportPipelineTree();
        if (isMounted && res.data) {
          setPipelineTree(res.data);
        }
      } catch (err: any) {
        console.error('Failed to load report pipeline tree from backend DynamoDB:', err);
      } finally {
        if (isMounted) setIsTreeLoading(false);
      }
    };

    loadPipelineTree();
    return () => {
      isMounted = false;
    };
  }, []);

  // Helper to persist pipeline tree mutations directly to backend DynamoDB
  const persistTreeToBackend = async (newTree: ProcessNode[], successMsg?: string) => {
    setPipelineTree(newTree);
    setIsTreeSaving(true);
    try {
      await apiService.saveReportPipelineTree(newTree);
      if (successMsg) {
        dispatch(showNotification({ message: successMsg, severity: 'success' }));
      }
    } catch (err: any) {
      console.error('Failed to save pipeline tree to backend DynamoDB:', err);
      dispatch(showNotification({ message: 'Failed to persist pipeline tree to backend DynamoDB', severity: 'error' }));
    } finally {
      setIsTreeSaving(false);
    }
  };

  // Flattened tree ordered by hierarchy
  const orderedTreeNodes = useMemo(() => {
    const result: TreeNodeWithDepth[] = [];

    const appendChildren = (parentId: string, currentDepth: number) => {
      const children = pipelineTree.filter((n) => n.parentId === parentId);
      for (const child of children) {
        result.push({ node: child, depth: currentDepth });
        appendChildren(child.id, currentDepth + 1);
      }
    };

    appendChildren('base-data', 1);
    return result;
  }, [pipelineTree]);

  // Execute pipeline for a given node
  const handleExecute = useCallback(
    async (targetId: string) => {
      if (!fromDate || !toDate) {
        dispatch(showNotification({ message: 'Please select both From Date and To Date', severity: 'warning' }));
        return;
      }

      setLoading(true);
      setExecutionError(null);
      setSelectedNodeId(targetId);
      const startTime = performance.now();

      try {
        const payload = {
          fromDate: fromDate.format('YYYY-MM-DD'),
          toDate: toDate.format('YYYY-MM-DD'),
          targetProcessId: targetId === 'base-data' ? '' : targetId,
          pipelineTree: pipelineTree,
        };

        const res = await apiService.executeReportDataSource(payload);
        const elapsed = Math.round(performance.now() - startTime);

        setData(res.data || []);
        setLastExecutedNodeId(targetId);
        setExecutionTimeMs(elapsed);
        setPage(0);
        setExpandedRowIds({});

        dispatch(
          showNotification({
            message: `Pipeline executed successfully (${res.data?.length || 0} employees returned in ${elapsed}ms)`,
            severity: 'success',
          })
        );
      } catch (err: any) {
        console.error('Pipeline execution error:', err);
        const errMsg = err?.message || err?.response?.data?.message || err?.data?.message || 'Failed to execute pipeline';
        setExecutionError(errMsg);
        dispatch(showNotification({ message: errMsg, severity: 'error' }));
      } finally {
        setLoading(false);
      }
    },
    [fromDate, toDate, pipelineTree, dispatch]
  );

  // Modal actions
  const handleOpenFork = (pId: string, pName: string) => {
    setModalNode(null);
    setModalParentId(pId);
    setModalParentName(pName);
    setModalOpen(true);
  };

  const handleOpenEdit = (node: ProcessNode) => {
    setModalNode(node);
    const parent = pipelineTree.find((n) => n.id === node.parentId);
    setModalParentId(node.parentId);
    setModalParentName(parent ? parent.name : 'Base Data');
    setModalOpen(true);
  };

  const handleCloneNode = (node: ProcessNode) => {
    const clone: ProcessNode = {
      ...node,
      id: 'proc_' + Date.now().toString(36),
      name: `${node.name} (Copy)`,
    };
    const updated = [...pipelineTree, clone];
    persistTreeToBackend(updated, `Cloned process "${node.name}" (saved to backend DynamoDB)`);
  };

  const handleDeleteNode = (id: string, name: string) => {
    // Recursively collect all descendant IDs
    const toDelete = new Set<string>([id]);
    const findChildren = (parentId: string) => {
      pipelineTree
        .filter((n) => n.parentId === parentId)
        .forEach((child) => {
          toDelete.add(child.id);
          findChildren(child.id);
        });
    };
    findChildren(id);

    const updated = pipelineTree.filter((n) => !toDelete.has(n.id));
    if (selectedNodeId && toDelete.has(selectedNodeId)) {
      setSelectedNodeId('base-data');
    }
    persistTreeToBackend(updated, `Deleted process "${name}" and child branches (saved to backend DynamoDB)`);
  };

  const handleSaveModalNode = (savedNode: ProcessNode) => {
    const idx = pipelineTree.findIndex((n) => n.id === savedNode.id);
    let updated: ProcessNode[];
    if (idx >= 0) {
      updated = [...pipelineTree];
      updated[idx] = savedNode;
    } else {
      updated = [...pipelineTree, savedNode];
    }
    setModalOpen(false);
    setSelectedNodeId(savedNode.id);
    persistTreeToBackend(updated, `Saved process "${savedNode.name}" to backend DynamoDB`);
  };

  const handleResetTree = () => {
    if (window.confirm('Reset all process branches back to canonical Base Data in backend DynamoDB?')) {
      setSelectedNodeId('base-data');
      setData(null);
      persistTreeToBackend([], 'Pipeline tree reset in backend DynamoDB');
    }
  };

  // Autopsy Table filtering
  const filteredRecords = useMemo(() => {
    if (!data) return [];
    if (!searchQuery.trim()) return data;

    const query = searchQuery.toLowerCase().trim();
    return data.filter((emp) => {
      const idMatch = (emp.internalId || emp.employeeInternalId)?.toLowerCase().includes(query);
      const nameMatch = (emp.name || emp.employeeName)?.toLowerCase().includes(query);
      return idMatch || nameMatch;
    });
  }, [data, searchQuery]);

  // Discover dynamic Details columns (excluding base keys)
  const dynamicDetailCols = useMemo(() => {
    if (!data || data.length === 0) return [];
    const baseKeys = new Set([
      'Date',
      'inTime',
      'outTime',
      'isLate',
      'LateMinute',
      'isAbsence',
      'isOnLeave',
      'LeaveType',
      'PunchError',
      'isPresentInHoliday',
      'isHoliday',
      'isWeeklyOff',
      'shiftId',
      'shiftName',
      'workStartTime',
      'workEndTime',
      'shiftStartTime',
      'shiftEndTime',
      'graceMinutes',
    ]);
    const cols = new Set<string>();
    for (const emp of data) {
      if (emp.attandanceInfo) {
        for (const day of emp.attandanceInfo) {
          for (const k of Object.keys(day)) {
            if (!baseKeys.has(k)) cols.add(k);
          }
        }
      }
    }
    return Array.from(cols);
  }, [data]);

  // Cell renderer with error trap detection
  const renderCellValue = (val: any) => {
    if (val === null || val === undefined) return <Typography variant="caption" color="text.disabled">—</Typography>;
    if (typeof val === 'boolean') {
      return val ? (
        <Chip label="TRUE" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
      ) : (
        <Chip label="FALSE" size="small" color="default" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
      );
    }

    const str = String(val);
    if (str.startsWith('#ERR:')) {
      return (
        <Tooltip title={`Runtime Calculation Error: ${str}`}>
          <Chip
            icon={<ErrorIcon sx={{ fontSize: '13px !important' }} />}
            label={str}
            size="small"
            color="error"
            sx={{ height: 22, fontSize: '0.72rem', fontFamily: 'monospace' }}
          />
        </Tooltip>
      );
    }

    return <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>{str}</Typography>;
  };

  // Export handlers
  const handleExportJson = () => {
    if (!data || data.length === 0) return;
    const jsonStr = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report_data_source_${selectedNodeId}_${dayjs().format('YYYYMMDD_HHmmss')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const availableCsvCols = useMemo(() => {
    return getAvailableCsvColumns(data || []);
  }, [data]);

  const filteredMainCsvCols = useMemo(() => {
    const q = csvColSearch.trim().toLowerCase();
    if (!q) return availableCsvCols.mainKeys;
    return availableCsvCols.mainKeys.filter((k) => k.toLowerCase().includes(q));
  }, [availableCsvCols.mainKeys, csvColSearch]);

  const filteredDetailCsvCols = useMemo(() => {
    const q = csvColSearch.trim().toLowerCase();
    if (!q) return availableCsvCols.detailKeys;
    return availableCsvCols.detailKeys.filter((k) => k.toLowerCase().includes(q));
  }, [availableCsvCols.detailKeys, csvColSearch]);

  const openCsvModal = (action: 'download' | 'copy') => {
    if (!data || data.length === 0) return;
    const available = getAvailableCsvColumns(data);
    const allCols = [...available.mainKeys, ...available.detailKeys];

    let initialSelected = allCols;
    try {
      const saved = localStorage.getItem('report_datasource_csv_selected_columns');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const validSaved = parsed.filter((c: string) => allCols.includes(c));
          if (validSaved.length > 0) {
            initialSelected = validSaved;
          }
        }
      }
    } catch (e) {
      console.warn('Failed to parse saved CSV columns from localStorage', e);
    }

    try {
      const savedAvoid = localStorage.getItem('report_datasource_csv_avoid_absent');
      if (savedAvoid !== null) {
        setAvoidAbsent(savedAvoid === 'true');
      }
    } catch (e) { }

    setCsvModalAction(action);
    setSelectedCsvCols(initialSelected);
    setCsvColSearch('');
    setCsvModalOpen(true);
  };

  const handleToggleAvoidAbsent = (checked: boolean) => {
    setAvoidAbsent(checked);
    try {
      localStorage.setItem('report_datasource_csv_avoid_absent', String(checked));
    } catch (e) { }
  };

  const exportStats = useMemo(() => {
    if (!data || data.length === 0) {
      return { includedRows: 0, excludedAbsences: 0, totalEmployees: 0, activeEmployees: 0 };
    }
    let includedRows = 0;
    let excludedAbsences = 0;
    let activeEmployees = 0;

    for (const emp of data) {
      const dailyRecords = emp.attandanceInfo || emp.attendanceInfo;
      if (!dailyRecords || dailyRecords.length === 0) {
        if (!avoidAbsent) {
          includedRows += 1;
        }
        continue;
      }

      let empHasPresence = false;
      for (const day of dailyRecords) {
        if (day.isAbsence) {
          if (avoidAbsent) {
            excludedAbsences += 1;
          } else {
            includedRows += 1;
          }
        } else {
          empHasPresence = true;
          includedRows += 1;
        }
      }
      if (empHasPresence || !avoidAbsent) {
        activeEmployees += 1;
      }
    }

    return {
      includedRows,
      excludedAbsences,
      totalEmployees: data.length,
      activeEmployees,
    };
  }, [data, avoidAbsent]);

  const toggleCsvCol = (col: string) => {
    setSelectedCsvCols((prev) => {
      const next = prev.includes(col) ? prev.filter((c) => c !== col) : [...prev, col];
      try {
        localStorage.setItem('report_datasource_csv_selected_columns', JSON.stringify(next));
      } catch (e) { }
      return next;
    });
  };

  const handleToggleGroupCols = (cols: string[], select: boolean) => {
    setSelectedCsvCols((prev) => {
      let next: string[];
      if (select) {
        const toAdd = cols.filter((c) => !prev.includes(c));
        next = [...prev, ...toAdd];
      } else {
        const removeSet = new Set(cols);
        next = prev.filter((c) => !removeSet.has(c));
      }
      try {
        localStorage.setItem('report_datasource_csv_selected_columns', JSON.stringify(next));
      } catch (e) { }
      return next;
    });
  };

  const handleSelectAllCsvCols = () => {
    const all = [...availableCsvCols.mainKeys, ...availableCsvCols.detailKeys];
    setSelectedCsvCols(all);
    try {
      localStorage.setItem('report_datasource_csv_selected_columns', JSON.stringify(all));
    } catch (e) { }
  };

  const handleDeselectAllCsvCols = () => {
    setSelectedCsvCols([]);
    try {
      localStorage.setItem('report_datasource_csv_selected_columns', JSON.stringify([]));
    } catch (e) { }
  };

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setSelectedCsvCols((items) => {
        const oldIndex = items.indexOf(String(active.id));
        const newIndex = items.indexOf(String(over.id));
        const next = arrayMove(items, oldIndex, newIndex);
        try {
          localStorage.setItem('report_datasource_csv_selected_columns', JSON.stringify(next));
        } catch (e) { }
        return next;
      });
    }
  };

  const handleMoveCol = (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= selectedCsvCols.length) return;
    const next = [...selectedCsvCols];
    const temp = next[index];
    next[index] = next[targetIndex];
    next[targetIndex] = temp;
    setSelectedCsvCols(next);
    try {
      localStorage.setItem('report_datasource_csv_selected_columns', JSON.stringify(next));
    } catch (e) { }
  };

  const handleSortAlphabetical = (ascending: boolean = true) => {
    const sorted = [...selectedCsvCols].sort((a, b) => {
      return ascending ? a.localeCompare(b) : b.localeCompare(a);
    });
    setSelectedCsvCols(sorted);
    try {
      localStorage.setItem('report_datasource_csv_selected_columns', JSON.stringify(sorted));
    } catch (e) { }
  };

  const handleResetToDefaultOrder = () => {
    const all = [...availableCsvCols.mainKeys, ...availableCsvCols.detailKeys];
    const set = new Set(selectedCsvCols);
    const reordered = all.filter((c) => set.has(c));
    setSelectedCsvCols(reordered);
    try {
      localStorage.setItem('report_datasource_csv_selected_columns', JSON.stringify(reordered));
    } catch (e) { }
  };

  const handleExecuteCsvDownload = () => {
    if (!data || data.length === 0) return;
    if (selectedCsvCols.length === 0) {
      dispatch(showNotification({ message: 'Please select at least one column to export', severity: 'warning' }));
      return;
    }
    const csvContent = flattenReportDataToCsv(data, selectedCsvCols, { avoidAbsent });
    downloadCsvFile(csvContent, `report_flattened_${selectedNodeId}_${dayjs().format('YYYYMMDD_HHmmss')}.csv`);
    dispatch(showNotification({
      message: avoidAbsent
        ? `CSV exported (${exportStats.excludedAbsences > 0 ? `${exportStats.excludedAbsences} absences excluded` : 'absences excluded'})`
        : 'CSV exported',
      severity: 'success',
    }));
    setCsvModalOpen(false);
  };

  const handleExecuteCsvCopy = async () => {
    if (!data || data.length === 0) return;
    if (selectedCsvCols.length === 0) {
      dispatch(showNotification({ message: 'Please select at least one column to export', severity: 'warning' }));
      return;
    }
    const csvContent = flattenReportDataToCsv(data, selectedCsvCols, { avoidAbsent });
    const ok = await copyTextToClipboard(csvContent);
    if (ok) {
      dispatch(showNotification({
        message: avoidAbsent
          ? 'CSV copied to clipboard (absent records excluded)'
          : 'CSV copied to clipboard',
        severity: 'success',
      }));
    } else {
      dispatch(showNotification({ message: 'Failed to copy to clipboard', severity: 'error' }));
    }
    setCsvModalOpen(false);
  };

  const toggleRowExpanded = (uuid: string) => {
    setExpandedRowIds((prev) => ({ ...prev, [uuid]: !prev[uuid] }));
  };

  const activeNodeName = useMemo(() => {
    if (selectedNodeId === 'base-data') return 'Base Data (Root)';
    const node = pipelineTree.find((n) => n.id === selectedNodeId);
    return node ? node.name : selectedNodeId;
  }, [selectedNodeId, pipelineTree]);

  return (
    <DashboardLayout>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, width: '100%', minWidth: 0, overflowX: 'hidden' }}>
        {/* Top Control Bar: Temporal Span & Execution Header */}
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: '16px !important' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h5" fontWeight={700} color="primary.main">
                  Report Data Source & Pipeline Workspace
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Hierarchical DAG engine with canonical Base Data synthesis and real-time autopsy extraction.
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <DatePicker
                  label="From Date"
                  value={fromDate}
                  onChange={(d) => setFromDate(d)}
                  slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                />
                <DatePicker
                  label="To Date"
                  value={toDate}
                  onChange={(d) => setToDate(d)}
                  slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  size="medium"
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <PlayIcon />}
                  disabled={loading}
                  onClick={() => handleExecute(selectedNodeId)}
                  sx={{ px: 2.5, fontWeight: 600, textTransform: 'none' }}
                >
                  {loading ? 'Synthesizing...' : `Run Active: ${activeNodeName}`}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Two-Panel Layout */}
        <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start', width: '100%', minWidth: 0 }}>
          {/* Left Panel: Pipeline Hierarchy Tree */}
          <Card
            variant="outlined"
            sx={{
              width: 320,
              flexShrink: 0,
              borderRadius: 2,
              maxHeight: 'calc(100vh - 220px)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                p: 2,
                pb: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TreeIcon color="primary" fontSize="small" />
                <Typography variant="subtitle1" fontWeight={700}>
                  Pipeline Hierarchy
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {isTreeSaving ? (
                  <Tooltip title="Saving changes to backend DynamoDB...">
                    <CircularProgress size={16} />
                  </Tooltip>
                ) : (
                  <Tooltip title="Synchronized with backend DynamoDB">
                    <CloudDoneIcon color="success" sx={{ fontSize: 18 }} />
                  </Tooltip>
                )}
                <Tooltip title="Reset all processes in backend DynamoDB">
                  <IconButton size="small" onClick={handleResetTree}>
                    <ResetIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {isTreeLoading && <LinearProgress color="primary" sx={{ height: 2 }} />}

            <Box sx={{ p: 1.5, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* Root Node: Base Data */}
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  cursor: 'pointer',
                  borderColor: selectedNodeId === 'base-data' ? 'primary.main' : 'divider',
                  borderWidth: selectedNodeId === 'base-data' ? 2 : 1,
                  bgcolor: selectedNodeId === 'base-data' ? 'primary.50' : 'background.paper',
                  transition: 'all 0.15s ease-in-out',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
                onClick={() => setSelectedNodeId('base-data')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label="ROOT" size="small" color="primary" sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700 }} />
                    <Typography variant="body2" fontWeight={700}>
                      Base Data
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip title="Run Base Data">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExecute('base-data');
                        }}
                      >
                        <PlayIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Fork child process from Base Data">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenFork('base-data', 'Base Data');
                        }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Micro-sources A to F canonical bi-level synthesis.
                </Typography>
              </Paper>

              {/* Child Process Nodes */}
              {orderedTreeNodes.map(({ node, depth }) => {
                const isSelected = selectedNodeId === node.id;
                const totalFilters = (node.mainFilters?.length || 0) + (node.detailsFilters?.length || 0);
                const totalCalcs = (node.mainCalculations?.length || 0) + (node.detailsCalculations?.length || 0);

                return (
                  <Paper
                    key={node.id}
                    variant="outlined"
                    sx={{
                      ml: (depth - 1) * 2,
                      p: 1.5,
                      borderRadius: 1.5,
                      cursor: 'pointer',
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      borderWidth: isSelected ? 2 : 1,
                      bgcolor: isSelected ? 'primary.50' : 'background.paper',
                      transition: 'all 0.15s ease-in-out',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                    onClick={() => setSelectedNodeId(node.id)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                      <Box sx={{ minWidth: 0, pr: 1 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {node.name}
                        </Typography>
                        {node.description && (
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                            {node.description}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                          <Chip
                            label={`${totalFilters} filters`}
                            size="small"
                            variant="outlined"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                          <Chip
                            label={`${totalCalcs} calcs`}
                            size="small"
                            variant="outlined"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Tooltip title="Run this process">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleExecute(node.id);
                            }}
                          >
                            <PlayIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Fork child from here">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenFork(node.id, node.name);
                            }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit process">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenEdit(node);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Clone">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCloneNode(node);
                            }}
                          >
                            <CloneIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteNode(node.id, node.name);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          </Card>

          {/* Right Panel: Data Autopsy & Export */}
          <Card variant="outlined" sx={{ flexGrow: 1, borderRadius: 2, minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
            {/* Action Bar */}
            <Box
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="h6" fontWeight={700}>
                  Data Autopsy
                </Typography>
                <Chip
                  label={activeNodeName}
                  color={selectedNodeId === 'base-data' ? 'primary' : 'secondary'}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
                {lastExecutedNodeId && (
                  <Typography variant="caption" color="text.secondary">
                    {data ? `${data.length} employees (${data.reduce((acc, e) => acc + (e.attandanceInfo?.length || 0), 0)} daily records)` : '0 records'}
                    {executionTimeMs !== null && ` • Executed in ${executionTimeMs}ms`}
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  placeholder="Search employee..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: 220, '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  disabled={!data || data.length === 0}
                  onClick={handleExportJson}
                >
                  JSON
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<CsvIcon />}
                  disabled={!data || data.length === 0}
                  onClick={() => openCsvModal('download')}
                >
                  CSV
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<CopyIcon />}
                  disabled={!data || data.length === 0}
                  onClick={() => openCsvModal('copy')}
                >
                  Copy CSV
                </Button>
              </Box>
            </Box>

            {/* Error banner */}
            {executionError && (
              <Box sx={{ p: 2 }}>
                <Alert severity="error">{executionError}</Alert>
              </Box>
            )}

            {/* Loading Indicator */}
            {loading && <LinearProgress color="primary" />}

            {/* Empty or Loaded Table View */}
            {!data && !loading ? (
              <Box sx={{ p: 8, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary" fontWeight={500}>
                  No Pipeline Data Rendered
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 480, mx: 'auto' }}>
                  Select <strong>Base Data</strong> or any configured process from the left panel and click{' '}
                  <strong>Run Active</strong> to synthesize records and evaluate expressions.
                </Typography>
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: 'calc(100vh - 310px)', width: '100%', overflowX: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 40, whiteSpace: 'nowrap' }} />
                      <TableCell sx={{ width: 40, whiteSpace: 'nowrap' }} />
                      <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', color: 'primary.main' }}>Internal ID</TableCell>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', color: 'primary.main' }}>Employee Name</TableCell>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', color: 'primary.main' }}>Biomatric Id</TableCell>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', color: 'primary.main' }}>Joining Date</TableCell>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', color: 'primary.main' }}>Resignation Date</TableCell>
                      <TableCell sx={{ fontWeight: 700, textAlign: 'right', whiteSpace: 'nowrap', color: 'primary.main' }}>Daily Records</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRecords
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((emp) => {
                        const isExpanded = !!expandedRowIds[emp.employeeUUID];
                        const dailyCount = emp.attandanceInfo?.length || 0;

                        return (
                          <React.Fragment key={emp.employeeUUID}>
                            <TableRow
                              hover
                              sx={{
                                cursor: 'pointer',
                                bgcolor: isExpanded ? 'action.hover' : 'inherit',
                              }}
                              onClick={() => toggleRowExpanded(emp.employeeUUID)}
                            >
                              <TableCell sx={{ width: 40, whiteSpace: 'nowrap' }}>
                                <IconButton size="small">
                                  {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                </IconButton>
                              </TableCell>
                              <TableCell sx={{ width: 40, whiteSpace: 'nowrap' }}>
                                <Tooltip title="View all employee information">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setInfoModalEmployee(emp);
                                    }}
                                  >
                                    <InfoIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{emp.internalId || emp.employeeInternalId || '—'}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{emp.employeeName || emp.name || '—'}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{emp.biometricId || emp.biomatricId || emp.employeeInternalId || emp.internalId || '—'}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{emp.joiningDate || '—'}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{emp.resignationDate || '—'}</TableCell>
                              <TableCell sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                <Chip
                                  label={`${dailyCount} days`}
                                  size="small"
                                  color={dailyCount > 0 ? 'primary' : 'default'}
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: '0.72rem' }}
                                />
                              </TableCell>
                            </TableRow>

                            {/* Subtable for Attendance Info */}
                            <TableRow>
                              <TableCell sx={{ py: 0 }} colSpan={8}>
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                  <Box sx={{ my: 1.5, mx: 2 }}>
                                    <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                                      Daily Attendance & Shift Details for {emp.name} ({emp.internalId})
                                    </Typography>
                                    {dailyCount === 0 ? (
                                      <Typography variant="caption" color="text.disabled">
                                        No daily attendance records retained for this employee.
                                      </Typography>
                                    ) : (
                                      <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
                                        <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                                          <Table size="small">
                                            <TableHead>
                                              <TableRow sx={{ bgcolor: 'grey.50' }}>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', whiteSpace: 'nowrap', color: 'primary.main' }}>Date</TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', whiteSpace: 'nowrap', color: 'primary.main' }}>Shift</TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', whiteSpace: 'nowrap', color: 'primary.main' }}>In Time</TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', whiteSpace: 'nowrap', color: 'primary.main' }}>Out Time</TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', whiteSpace: 'nowrap', color: 'primary.main' }}>Status</TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', whiteSpace: 'nowrap', color: 'primary.main' }}>Late (min)</TableCell>
                                                {dynamicDetailCols.map((col) => (
                                                  <TableCell
                                                    key={col}
                                                    sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'primary.main', whiteSpace: 'nowrap' }}
                                                  >
                                                    {col}
                                                  </TableCell>
                                                ))}
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {emp.attandanceInfo.map((day: AttendanceInfoRecord, dIdx: number) => (
                                                <TableRow key={day.Date || dIdx} hover>
                                                  <TableCell sx={{ fontSize: '0.78rem', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                                    {day.Date}
                                                  </TableCell>
                                                  <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                                                    {day.shiftName ? `${day.shiftName}${day.workStartTime ? ` (${day.workStartTime} - ${day.workEndTime})` : ''}` : '—'}
                                                  </TableCell>
                                                  <TableCell sx={{ fontSize: '0.78rem', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                                    {day.inTime || '—'}
                                                  </TableCell>
                                                  <TableCell sx={{ fontSize: '0.78rem', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                                    {day.outTime || '—'}
                                                  </TableCell>
                                                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                      {day.isLate && (
                                                        <Chip
                                                          label="Late"
                                                          size="small"
                                                          color="warning"
                                                          sx={{ height: 18, fontSize: '0.65rem' }}
                                                        />
                                                      )}
                                                      {day.isAbsence && (
                                                        <Chip
                                                          label="Absence"
                                                          size="small"
                                                          color="error"
                                                          sx={{ height: 18, fontSize: '0.65rem' }}
                                                        />
                                                      )}
                                                      {day.isOnLeave && (
                                                        <Chip
                                                          label={`Leave: ${day.LeaveType || ''}`}
                                                          size="small"
                                                          color="info"
                                                          sx={{ height: 18, fontSize: '0.65rem' }}
                                                        />
                                                      )}
                                                      {day.isHoliday && (
                                                        <Chip
                                                          label="Holiday"
                                                          size="small"
                                                          color="success"
                                                          sx={{ height: 18, fontSize: '0.65rem' }}
                                                        />
                                                      )}
                                                      {day.isWeeklyOff && (
                                                        <Chip
                                                          label="Weekly Off"
                                                          size="small"
                                                          variant="outlined"
                                                          sx={{ height: 18, fontSize: '0.65rem' }}
                                                        />
                                                      )}
                                                      {!day.isLate && !day.isAbsence && !day.isOnLeave && !day.isHoliday && !day.isWeeklyOff && (
                                                        <Chip
                                                          label="Present"
                                                          size="small"
                                                          color="success"
                                                          variant="outlined"
                                                          sx={{ height: 18, fontSize: '0.65rem' }}
                                                        />
                                                      )}
                                                    </Box>
                                                  </TableCell>
                                                  <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                                                    {day.LateMinute > 0 ? day.LateMinute : '—'}
                                                  </TableCell>
                                                  {dynamicDetailCols.map((col) => (
                                                    <TableCell key={col} sx={{ whiteSpace: 'nowrap' }}>{renderCellValue(day[col])}</TableCell>
                                                  ))}
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        </TableContainer>
                                      </Paper>
                                    )}
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Pagination */}
            {data && data.length > 0 && (
              <TablePagination
                component="div"
                count={filteredRecords.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            )}
          </Card>
        </Box>
      </Box>

      {/* Configuration Modal for Add Fork & Edit */}
      <ProcessConfigModal
        open={modalOpen}
        node={modalNode}
        parentId={modalParentId}
        parentName={modalParentName}
        pipelineTree={pipelineTree}
        onSave={handleSaveModalNode}
        onClose={() => setModalOpen(false)}
      />

      {/* Employee Information Modal */}
      <Dialog
        open={Boolean(infoModalEmployee)}
        onClose={() => setInfoModalEmployee(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon color="primary" fontSize="small" />
            <Typography variant="h6" fontWeight={700}>
              Employee Information
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setInfoModalEmployee(null)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, maxHeight: '70vh' }}>
          {infoModalEmployee && (
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 700, width: '40%', color: 'primary.main' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(infoModalEmployee)
                  .filter(([key]) => key !== 'attandanceInfo' && key !== 'attendanceInfo')
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([key, val]) => (
                    <TableRow key={key} hover>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                        {key}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.82rem', wordBreak: 'break-word' }}>
                        {renderCellValue(val)}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button onClick={() => setInfoModalEmployee(null)} variant="outlined" size="small">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSV Column Selection & Sort Sequence Modal */}
      <Dialog
        open={csvModalOpen}
        onClose={() => setCsvModalOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CsvIcon color="primary" />
            <Typography variant="h6" fontWeight={700}>
              Customize CSV Columns & Sort Sequence
            </Typography>
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={`${selectedCsvCols.length} of ${availableCsvCols.mainKeys.length + availableCsvCols.detailKeys.length} selected`}
            />
          </Box>
          <IconButton size="small" onClick={() => setCsvModalOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2.5, maxHeight: '76vh', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Avoid Absent Employees Option Banner */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.25,
              bgcolor: avoidAbsent ? 'warning.50' : 'grey.50',
              border: '1px solid',
              borderColor: avoidAbsent ? 'warning.main' : 'divider',
              borderRadius: 1.5,
              flexWrap: 'wrap',
              gap: 1.5,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={avoidAbsent}
                  onChange={(e) => handleToggleAvoidAbsent(e.target.checked)}
                  color="warning"
                />
              }
              label={
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="subtitle2" fontWeight={700} color={avoidAbsent ? 'warning.dark' : 'text.primary'}>
                    Avoid Absent Employees
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Exclude absence records and omit employees who were not present during this period.
                  </Typography>
                </Box>
              }
              sx={{ m: 0 }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {avoidAbsent ? (
                <Chip
                  size="small"
                  color="warning"
                  variant="filled"
                  label={
                    exportStats.excludedAbsences > 0
                      ? `Excluding ${exportStats.excludedAbsences.toLocaleString()} absence records (${exportStats.includedRows.toLocaleString()} rows will be exported)`
                      : `Avoiding absences (${exportStats.includedRows.toLocaleString()} active rows)`
                  }
                  sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                />
              ) : (
                <Chip
                  size="small"
                  variant="outlined"
                  color="default"
                  label={`All ${exportStats.includedRows.toLocaleString()} rows included`}
                  sx={{ fontSize: '0.75rem' }}
                />
              )}
            </Box>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2.5,
              alignItems: 'stretch',
            }}
          >
            {/* Left Panel: Available Columns */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                p: 2,
                minHeight: 400,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={700} color="primary.main">
                  1. Choose Columns
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Button size="small" variant="text" sx={{ fontSize: '0.75rem', py: 0 }} onClick={handleSelectAllCsvCols}>
                    Select All
                  </Button>
                  <Button size="small" variant="text" color="inherit" sx={{ fontSize: '0.75rem', py: 0 }} onClick={handleDeselectAllCsvCols}>
                    Deselect All
                  </Button>
                </Box>
              </Box>

              <TextField
                size="small"
                placeholder="Search columns..."
                value={csvColSearch}
                onChange={(e) => setCsvColSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1.5 }}
              />

              <Box sx={{ flex: 1, overflowY: 'auto', maxHeight: '52vh', pr: 0.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Section 1: Employee Columns */}
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" fontWeight={700} color="text.primary">
                        Employee Columns
                      </Typography>
                      <Chip
                        size="small"
                        label={`${availableCsvCols.mainKeys.filter((k) => selectedCsvCols.includes(k)).length} / ${availableCsvCols.mainKeys.length}`}
                        sx={{ height: 18, fontSize: '0.68rem' }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Button
                        size="small"
                        sx={{ fontSize: '0.72rem', py: 0 }}
                        onClick={() => handleToggleGroupCols(availableCsvCols.mainKeys, true)}
                      >
                        All
                      </Button>
                      <Button
                        size="small"
                        color="inherit"
                        sx={{ fontSize: '0.72rem', py: 0 }}
                        onClick={() => handleToggleGroupCols(availableCsvCols.mainKeys, false)}
                      >
                        None
                      </Button>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                      gap: 0.5,
                    }}
                  >
                    {filteredMainCsvCols.map((col) => {
                      const checked = selectedCsvCols.includes(col);
                      const seqIdx = selectedCsvCols.indexOf(col);
                      return (
                        <FormControlLabel
                          key={col}
                          control={
                            <Checkbox
                              size="small"
                              checked={checked}
                              onChange={() => toggleCsvCol(col)}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>{col}</Typography>
                              {checked && (
                                <Chip
                                  size="small"
                                  label={`#${seqIdx + 1}`}
                                  color="primary"
                                  variant="outlined"
                                  sx={{ height: 16, fontSize: '0.62rem', fontWeight: 700 }}
                                />
                              )}
                            </Box>
                          }
                        />
                      );
                    })}
                    {filteredMainCsvCols.length === 0 && (
                      <Typography variant="caption" color="text.disabled" sx={{ p: 1 }}>
                        No matching employee columns
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Section 2: Attendance Columns */}
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" fontWeight={700} color="text.primary">
                        Daily Attendance Columns
                      </Typography>
                      <Chip
                        size="small"
                        label={`${availableCsvCols.detailKeys.filter((k) => selectedCsvCols.includes(k)).length} / ${availableCsvCols.detailKeys.length}`}
                        sx={{ height: 18, fontSize: '0.68rem' }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Button
                        size="small"
                        sx={{ fontSize: '0.72rem', py: 0 }}
                        onClick={() => handleToggleGroupCols(availableCsvCols.detailKeys, true)}
                      >
                        All
                      </Button>
                      <Button
                        size="small"
                        color="inherit"
                        sx={{ fontSize: '0.72rem', py: 0 }}
                        onClick={() => handleToggleGroupCols(availableCsvCols.detailKeys, false)}
                      >
                        None
                      </Button>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                      gap: 0.5,
                    }}
                  >
                    {filteredDetailCsvCols.map((col) => {
                      const checked = selectedCsvCols.includes(col);
                      const seqIdx = selectedCsvCols.indexOf(col);
                      return (
                        <FormControlLabel
                          key={col}
                          control={
                            <Checkbox
                              size="small"
                              checked={checked}
                              onChange={() => toggleCsvCol(col)}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>{col}</Typography>
                              {checked && (
                                <Chip
                                  size="small"
                                  label={`#${seqIdx + 1}`}
                                  color="secondary"
                                  variant="outlined"
                                  sx={{ height: 16, fontSize: '0.62rem', fontWeight: 700 }}
                                />
                              )}
                            </Box>
                          }
                        />
                      );
                    })}
                    {filteredDetailCsvCols.length === 0 && (
                      <Typography variant="caption" color="text.disabled" sx={{ p: 1 }}>
                        No matching attendance columns
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Right Panel: Export Sequence */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                p: 2,
                bgcolor: 'grey.50',
                minHeight: 400,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700} color="primary.main">
                    2. Column Sort Sequence
                  </Typography>
                  <Chip
                    size="small"
                    color="primary"
                    label={`${selectedCsvCols.length} columns`}
                    sx={{ height: 20, fontSize: '0.72rem', fontWeight: 600 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Tooltip title="Sort Alphabetically (A → Z)">
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      startIcon={<SortAlphaIcon sx={{ fontSize: '0.9rem' }} />}
                      sx={{ fontSize: '0.72rem', py: 0.25, px: 0.75 }}
                      onClick={() => handleSortAlphabetical(true)}
                      disabled={selectedCsvCols.length < 2}
                    >
                      A → Z
                    </Button>
                  </Tooltip>
                  <Tooltip title="Reset to Default Order (Employee first, then Attendance)">
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      startIcon={<ResetIcon sx={{ fontSize: '0.9rem' }} />}
                      sx={{ fontSize: '0.72rem', py: 0.25, px: 0.75 }}
                      onClick={handleResetToDefaultOrder}
                      disabled={selectedCsvCols.length < 2}
                    >
                      Default
                    </Button>
                  </Tooltip>
                  <Tooltip title="Clear all columns">
                    <Button
                      size="small"
                      variant="text"
                      color="error"
                      sx={{ fontSize: '0.72rem', py: 0.25, px: 0.75 }}
                      onClick={handleDeselectAllCsvCols}
                      disabled={selectedCsvCols.length === 0}
                    >
                      Clear
                    </Button>
                  </Tooltip>
                </Box>
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                Columns will appear in this exact sequence from left to right. Drag by handle or use arrows to rearrange.
              </Typography>

              {/* Scrollable Sequence List */}
              <Box sx={{ flex: 1, overflowY: 'auto', maxHeight: '52vh', pr: 0.5 }}>
                {selectedCsvCols.length === 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 260,
                      border: '1px dashed',
                      borderColor: 'divider',
                      borderRadius: 1.5,
                      p: 3,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ mb: 0.5 }}>
                      No columns selected
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      Select columns from the left panel to build your export sequence.
                    </Typography>
                  </Box>
                ) : (
                  <DndContext
                    sensors={dndSensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext items={selectedCsvCols} strategy={verticalListSortingStrategy}>
                      {selectedCsvCols.map((col, idx) => {
                        const isDetail = availableCsvCols.detailKeys.includes(col);
                        return (
                          <SortableColumnItem
                            key={col}
                            id={col}
                            index={idx}
                            isDetail={isDetail}
                            onMoveUp={() => handleMoveCol(idx, 'up')}
                            onMoveDown={() => handleMoveCol(idx, 'down')}
                            onRemove={() => toggleCsvCol(col)}
                            canMoveUp={idx > 0}
                            canMoveDown={idx < selectedCsvCols.length - 1}
                          />
                        );
                      })}
                    </SortableContext>
                  </DndContext>
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5, justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Button onClick={() => setCsvModalOpen(false)} variant="outlined" color="inherit" size="small">
              Cancel
            </Button>
            <Typography variant="caption" color="text.secondary">
              {avoidAbsent
                ? `Exporting ${exportStats.includedRows.toLocaleString()} rows (${exportStats.excludedAbsences.toLocaleString()} absences excluded)`
                : `Exporting ${exportStats.includedRows.toLocaleString()} total rows`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={handleExecuteCsvCopy}
              variant={csvModalAction === 'copy' ? 'contained' : 'outlined'}
              startIcon={<CopyIcon />}
              size="small"
              disabled={selectedCsvCols.length === 0 || exportStats.includedRows === 0}
            >
              Copy to Clipboard
            </Button>
            <Button
              onClick={handleExecuteCsvDownload}
              variant={csvModalAction === 'download' ? 'contained' : 'outlined'}
              startIcon={<CsvIcon />}
              size="small"
              disabled={selectedCsvCols.length === 0 || exportStats.includedRows === 0}
            >
              Download CSV
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default ReportDataSource;
